import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';
import { Container as MapDiv, NaverMap, Marker, useNavermaps } from 'react-naver-maps';

/**
 * 내진설계지원 - STEP 1: 시추공 선택 화면
 * 드래그/탐색 모드로 지도에서 시추공을 선택하고 "다음" 버튼으로 확정
 */
function SeismicDesignSupport({ onNext }) {
  const navermaps = useNavermaps();
  const [map, setMap] = useState(null);
  const [draggable, setDraggable] = useState(true); // true: 탐색 모드, false: 드래그 선택 모드
  const [allBoreholes, setAllBoreholes] = useState([]);
  const [selectedBoreholes, setSelectedBoreholes] = useState([]);

  useEffect(() => {
    fetchAllBoreholes();
  }, []);

  // 모든 borehole 데이터를 가져오는 함수
  const fetchAllBoreholes = () => {
    const token = localStorage.getItem('token');
    axios.get('/boreholes/', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => {
        setAllBoreholes(response.data.data);
      })
      .catch(error => {
        console.error('Borehole 데이터를 가져오는 중 오류 발생:', error);
      });
  };

  // draggable 변경 시 지도 옵션만 업데이트 (중심 유지)
  useEffect(() => {
    if (map) {
      map.setOptions({ draggable });
    }
  }, [draggable, map]);

  // 지도에서 마우스 이벤트 등록
  useEffect(() => {
    let startPoint = null;
    let selectionBox = null;

    if (!draggable && map) {
      const handleMouseDown = (e) => {
        if (!draggable) {
          startPoint = e.coord;  // Start point of selection
          // Initialize selection box (you can style it with CSS)
          selectionBox = new navermaps.Rectangle({
            map: map,
            bounds: new navermaps.LatLngBounds(startPoint, startPoint),
            strokeColor: "#FF0000",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#FF0000",
            fillOpacity: 0.2,
          });
        }
      };

      const handleMouseMove = (e) => {
        if (!draggable && startPoint && selectionBox) {
          const currentPoint = e.coord;
          // Update the selection box based on current mouse position
          selectionBox.setBounds(new navermaps.LatLngBounds(startPoint, currentPoint));
        }
      };

      const handleMouseUp = (e) => {
        console.log('Mouse up!');
        if (!draggable && selectionBox) {
          // Finalize the selection and get boreholes within the selection
          const bounds = selectionBox.getBounds();
          console.log("bounds: ", bounds)

          const selectedBoreholes = allBoreholes.filter(borehole => {
            console.log("borehole: ", borehole.grs80_x, borehole.grs80_y)
            const latLng = new navermaps.LatLng(borehole.grs80_x, borehole.grs80_y);
            return bounds.hasLatLng(latLng);
          });
          console.log("selectedBoreholes: ", selectedBoreholes)
          setSelectedBoreholes(selectedBoreholes);
          // Remove the selection box after selection
          selectionBox.setMap(null);
          selectionBox = null;
        }
        startPoint = null;
      };


      const handleDoubleClick = () => {
        console.log('Double click!');
        setSelectedBoreholes([]); // 더블 클릭 시 선택된 마커 초기화
      };

      // 리스너 추가
      const mouseDownListener = navermaps.Event.addListener(map, 'mousedown', handleMouseDown);
      const mouseMoveListener = navermaps.Event.addListener(map, 'mousemove', handleMouseMove);
      const mouseUpListener = navermaps.Event.addListener(map, 'mouseup', handleMouseUp);
      const doubleClickListener = navermaps.Event.addListener(map, 'dblclick', handleDoubleClick);

      console.log('Listeners registered');

      // cleanup: 리스너 제거
      return () => {
        console.log('Cleaning up listeners');
        if (map) {
          navermaps.Event.removeListener(mouseDownListener);
          navermaps.Event.removeListener(mouseMoveListener);
          navermaps.Event.removeListener(mouseUpListener);
          navermaps.Event.removeListener(doubleClickListener);
        }

      };
    }
  }, [draggable, map, allBoreholes, navermaps]);

  const handleNext = () => {
    if (selectedBoreholes.length === 0) {
      alert('시추공을 선택해주세요.');
      return;
    }
    onNext(selectedBoreholes);
  };

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">내진설계지원</h1>
          <p className="text-secondary-600">
            STEP 1: 분석할 시추공을 선택하세요
          </p>
        </div>

        {/* 지도 컨테이너 */}
        <div className="bg-white rounded-2xl shadow-medium overflow-hidden" style={{ height: '600px' }}>
          <div className="relative w-full h-full">
            {/* 지도 */}
            <MapDiv style={{ width: '100%', height: '100%' }}>
              <NaverMap
                defaultCenter={new navermaps.LatLng(36.5, 127.5)}
                defaultZoom={7}
                ref={setMap}
              >
                {/* 모든 시추공 마커 표시 */}
                {allBoreholes.map(borehole => {
                  const isSelected = selectedBoreholes.some(sb => sb.id === borehole.id);
                  return (
                    <Marker
                      key={borehole.id}
                      position={new navermaps.LatLng(borehole.grs80_x, borehole.grs80_y)}
                      icon={{
                        content: `<div style="
                          width: 24px;
                          height: 24px;
                          background-color: ${isSelected ? '#FF0000' : '#2780E3'};
                          border: 3px solid white;
                          border-radius: 50%;
                          box-shadow: 0 3px 6px rgba(0,0,0,0.5);
                          cursor: pointer;
                        "></div>`,
                        anchor: new navermaps.Point(12, 12),
                      }}
                    />
                  );
                })}
              </NaverMap>

              {/* 좌측 상단 컨트롤 */}
              <div className="absolute top-4 left-4 z-10">
                <div className="bg-white rounded-xl shadow-medium p-1 flex items-center">
                  <button
                    onClick={() => setDraggable(true)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center space-x-2 ${
                      draggable
                        ? 'bg-primary-600 text-white'
                        : 'text-secondary-600 hover:bg-secondary-100'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>탐색</span>
                  </button>
                  <button
                    onClick={() => setDraggable(false)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center space-x-2 ${
                      !draggable
                        ? 'bg-red-600 text-white'
                        : 'text-secondary-600 hover:bg-secondary-100'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>선택</span>
                  </button>
                </div>
              </div>

              {/* 우측 하단 정보 */}
              <div className="absolute bottom-4 right-4 z-10 bg-white rounded-xl shadow-hard p-4">
                <p className="text-sm font-semibold text-secondary-900 mb-2">
                  선택된 시추공: <span className="text-primary-600">{selectedBoreholes.length}개</span>
                </p>
                <p className="text-xs text-secondary-600 mb-3">
                  {draggable
                    ? '드래그 선택 모드로 전환하여 시추공을 선택하세요'
                    : '지도에서 드래그하여 시추공을 선택하세요 (더블클릭으로 초기화)'}
                </p>
                <button
                  onClick={handleNext}
                  disabled={selectedBoreholes.length === 0}
                  className={`w-full px-4 py-2 rounded-xl font-semibold transition-all ${
                    selectedBoreholes.length === 0
                      ? 'bg-secondary-300 text-secondary-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:shadow-hard'
                  }`}
                >
                  다음 단계로 →
                </button>
              </div>
            </MapDiv>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SeismicDesignSupport;
